import { bindable, customElement } from 'aurelia';
import { I18N } from '@aurelia/i18n';
import { resolve } from 'aurelia';
import template from './rule-modal.html';
import type { Rule } from '../services/api.types';
import { createRule, updateRule } from '../services/api.service';

@customElement({ name: 'rule-modal', template })
export class RuleModal {
  private readonly i18n: I18N = resolve(I18N);

  @bindable public saved!: (rule: Rule) => void;
  @bindable public closed!: () => void;
  @bindable public ruleToEdit: Rule | null = null;

  public title = '';
  public selector = '';
  public code = DEFAULT_CODE;
  public saving = false;
  public error = '';
  public selectorError = '';

  private _keyHandler!: (e: KeyboardEvent) => void;

  public get isEditMode(): boolean {
    return this.ruleToEdit !== null;
  }

  public attached(): void {
    if (this.ruleToEdit) {
      this.title    = this.ruleToEdit.title;
      this.selector = this.ruleToEdit.selector;
      this.code     = this.ruleToEdit.code;
    }
    this._keyHandler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') this.close();
    };
    document.addEventListener('keydown', this._keyHandler);
  }

  public detached(): void {
    document.removeEventListener('keydown', this._keyHandler);
  }

  public close(): void {
    this.closed();
  }

  public validateSelector(): void {
    this.selectorError = '';
    if (!this.selector.trim()) return;
    try {
      new RegExp(this.selector);
    } catch {
      this.selectorError = this.i18n.tr('rules.form.selectorInvalid');
    }
  }

  public async save(): Promise<void> {
    this.error = '';
    this.selectorError = '';

    if (!this.title.trim()) {
      this.error = this.i18n.tr('rules.form.titleRequired');
      return;
    }
    if (!this.selector.trim()) {
      this.error = this.i18n.tr('rules.form.selectorRequired');
      return;
    }
    try {
      new RegExp(this.selector);
    } catch {
      this.selectorError = this.i18n.tr('rules.form.selectorInvalid');
      return;
    }

    this.saving = true;
    try {
      let rule: Rule;
      if (this.isEditMode && this.ruleToEdit) {
        rule = await updateRule(this.ruleToEdit.id, {
          title:    this.title.trim(),
          selector: this.selector.trim(),
          code:     this.code,
        });
      } else {
        rule = await createRule({
          title:    this.title.trim(),
          selector: this.selector.trim(),
          code:     this.code,
        });
      }
      this.saved(rule);
    } catch (e: unknown) {
      this.error = e instanceof Error ? e.message : String(e);
    } finally {
      this.saving = false;
    }
  }
}

const DEFAULT_CODE = `// Available variables:
//   filename   - the name of the uploaded file (string)
//   readContent - async function that returns the file text (async () => string)
//   hasIndexedText - true when stored extracted text is available
//   readIndexedText - async function returning stored extracted PDF text
//   hasForm(title) - true when the document contains a readable form submission
//   findForm(title) - the first matching { fileId, form, data } object, or null
//   listForms() - all readable form submissions in the document
//   upsertForm(title, data) - add the form or replace the first matching submission
//
// Return an object with any of these optional fields:
//   { title, documentDate, tags, securityLevel, contentClassification }
//
// Examples:
//   return { title: filename.replace(/\\.pdf$/i, '') };
//   const text = hasIndexedText ? await readIndexedText() : '';
//   const m = text.match(/(\\d{4}-\\d{2}-\\d{2})/);
//   return { documentDate: m ? m[1] : null, tags: ['invoices'] };
//   if (!hasForm('Example')) {
//     upsertForm('Example', { 'Value': 42 });
//   }

return {
  title: filename.replace(/\\.[^.]+$/, ''),
};
`;
